import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";

/** GoTrue generates 6-digit OTPs when using send SMS hooks */
export const OTP_LENGTH = 6;

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  /**
   * Dev-only convenience: the on-screen "Dev OTP" mock code, as returned by
   * `useDevOtp` (which only resolves a value when `VITE_DEV_MODE=true`). When
   * provided, the boxes are auto-filled once with this code — and completed —
   * so manual and automated tests don't have to type it. It fires once per
   * distinct code, then steps aside so the field stays editable; a resent code
   * (a new value) auto-fills again. Mirrors the SMS Web-OTP autofill path.
   */
  devOtp?: string | null;
}

const slotClass =
  "w-12 h-14 text-2xl font-bold rounded-xl border-2 bg-white border-[#030712] !text-[#030712] data-[active=true]:border-[#791FDA] data-[active=true]:ring-0 data-[active=true]:ring-offset-0";

export function OtpInput({
  value,
  onChange,
  onComplete,
  disabled,
  className,
  autoFocus,
  devOtp,
}: OtpInputProps) {
  const abortRef = useRef<AbortController | null>(null);

  // Dev-only: auto-fill the on-screen mock OTP. `devOtp` is non-null only in
  // dev mode (gated upstream by `useDevOtp`), so this is inert in production.
  // Guarded to fire once per distinct code so it never fights manual editing,
  // while a resent (new) code still auto-fills.
  //
  // Why both `onChange` AND `onComplete` are fired explicitly: `input-otp`
  // *does* auto-fire onComplete when its controlled value transitions to
  // maxLength, but in jsdom + testing-library the library's internal
  // effect chain doesn't reliably reach that path on a synthetic prop
  // change (the ref-based usePrevious + the input-event dispatch chain
  // it relies on doesn't always settle inside vitest's render → effect
  // → assertion window). Firing onComplete explicitly here means the
  // dev-OTP autofill works the same in tests and in real dev builds.
  //
  // The well-known cost: in production the library MAY also fire
  // onComplete on the next render, producing a double-call. Consumers
  // (e.g. `Auth.handleVerifyOtp`) MUST single-flight their verify path
  // with a ref guard to absorb the second call. See the `verifyingRef`
  // pattern in `Auth.tsx` for the canonical example. Without that
  // guard the second verify hits a consumed OTP and surfaces auth-004
  // ("No active OTP found for this phone number") after a successful
  // login — the bug this comment exists to prevent re-introducing.
  const autofilledOtpRef = useRef<string | null>(null);
  useEffect(() => {
    if (!devOtp || devOtp.length !== OTP_LENGTH) {
      // `useDevOtp` clears the code to null before re-fetching on resend; reset
      // the guard here so an identical resent code still re-fills the boxes.
      autofilledOtpRef.current = null;
      return;
    }
    if (autofilledOtpRef.current === devOtp) return;
    autofilledOtpRef.current = devOtp;
    onChange(devOtp);
    onComplete?.(devOtp);
  }, [devOtp, onChange, onComplete]);

  useEffect(() => {
    if (!("OTPCredential" in window)) return;

    const ac = new AbortController();
    abortRef.current = ac;

    navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: ac.signal,
      } as CredentialRequestOptions)
      .then((credential: any) => {
        if (credential?.code) {
          // Same shape as the devOtp branch above — see its comment for why
          // both are fired and why consumers must single-flight.
          onChange(credential.code);
          onComplete?.(credential.code);
        }
      })
      .catch(() => {
        // User dismissed or AbortController cancelled — ignore
      });

    return () => {
      ac.abort();
    };
  }, [onChange, onComplete]);

  return (
    <div className={cn("flex justify-center", className)} dir="ltr">
      <InputOTP
        maxLength={OTP_LENGTH}
        value={value}
        onChange={onChange}
        onComplete={onComplete}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="one-time-code"
        inputMode="numeric"
      >
        <InputOTPGroup className="gap-2" dir="ltr">
          {Array.from({ length: OTP_LENGTH }, (_, i) => (
            <InputOTPSlot key={i} index={i} className={slotClass} />
          ))}
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
}
